import * as React from 'react';
import { Selectors, useOptIns, useAvailableOptIns } from '@grandvisionhq/auth';
import { useDispatch, useIntl, useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { setChangedOptIns } from '../../../actions.js';
import { shouldCreateAccount } from '../../../selectors.js';
import { ItemList } from '../components/ItemList.js';
import { toSavedOptIns, getShownOptIns, toChangedOptIns } from '../utils/opt-ins.js';
import { useCheckoutSettings } from '../../../context/CheckoutSettings/context.js';

const OptInsContainer = ({ handleOptInsLoading  })=>{
    const dispatch = useDispatch();
    const { getLabel , getFormattedLabel  } = useIntl();
    const { account , consent  } = useCheckoutSettings();
    const { isMyAccountEnabled  } = useFeatureToggles();
    const isLoggedIn = useSelector(Selectors.isLoggedIn);
    const shouldCreateAccountBoolean = useSelector(shouldCreateAccount);
    const [selectedOptIns, setSelectedOptIns] = React.useState([]);
    const { optInsFromServer , isFetching  } = useOptIns();
    const { availableOptIns  } = useAvailableOptIns();
    const savedOptIns = React.useMemo(()=>toSavedOptIns(optInsFromServer)
    , [
        optInsFromServer
    ]);
    React.useEffect(()=>{
        /**
     *  Callback to notify the consumer of OptInsContainer
     *  about OptIns loading status from GQL query
     *  In checkout the user doesn't have to be logged in to proceed
     */ handleOptInsLoading === null || handleOptInsLoading === void 0 ? void 0 : handleOptInsLoading({
            isOptInsLoading: isFetching
        });
    }, [
        isFetching,
        handleOptInsLoading
    ]);
    // this includes the optins that have been previously selected by the customer
    // and are stored in the backend
    const allOptIns = (consent === null || consent === void 0 ? void 0 : consent.optIns) || availableOptIns.filter((item)=>item.isInSignup
    );
    // do not include the optins the customer already opted in for
    const shownOptIns = React.useMemo(()=>getShownOptIns(allOptIns, savedOptIns)
    , [
        allOptIns,
        savedOptIns
    ]);
    const handleChange = React.useCallback((selectedItems)=>{
        setSelectedOptIns(selectedItems);
        const changedOptIns = toChangedOptIns(allOptIns, savedOptIns, selectedItems, getLabel);
        // store ungrouped optins ready to be saved in the redux state
        dispatch(setChangedOptIns(changedOptIns));
    }, [
        dispatch,
        getLabel,
        allOptIns,
        savedOptIns
    ]);
    const getItemLabel = React.useCallback((item)=>getFormattedLabel(item.titleKey, {
            parseHTML: true
        })
    , [
        getFormattedLabel
    ]);
    const shouldIndent = React.useMemo(()=>{
        return !isLoggedIn && shouldCreateAccountBoolean && !(account === null || account === void 0 ? void 0 : account.implicitAccountCreation);
    }, [
        account,
        isLoggedIn,
        shouldCreateAccountBoolean
    ]);
    const getItemIdentifier = React.useCallback((item)=>item.titleKey
    , []);
    /**
   * Do not display opt-ins on these conditions:
   * 1. My account disabled and no custom opt-ins provided from an opco
   * 2. Displayable opt-ins list is empty
   * 3. Customer is not logged in and the create account check mark is not checked
   */ if (!isMyAccountEnabled && !(consent === null || consent === void 0 ? void 0 : consent.optIns) || !shownOptIns.length || isMyAccountEnabled && !isLoggedIn && !shouldCreateAccountBoolean) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ItemList, {
        getItemIdentifier: getItemIdentifier,
        getItemLabel: getItemLabel,
        idPrefix: "opt-ins__",
        items: shownOptIns,
        onChange: handleChange,
        selectedItems: selectedOptIns,
        shouldIndent: shouldIndent
    });
};

export { OptInsContainer };
//# sourceMappingURL=OptInsContainer.js.map
